import React, {FC, useEffect} from "react";
import {Button, Grid, Table, TableBody, TableContainer, Typography} from "@mui/material";
import {useForm} from "react-hook-form";
import {useTranslation} from "react-i18next";
import FormSectionTitle from "../../../../../components/forms/FormSectionTitle";
import FormSelect from "../../../../../components/forms/FormSelect";
import FormTextField from "../../../../../components/forms/FormTextField";
import {SkeletonTableRow} from "../../../../../components/loaders/LoadingView";
import {FilterDTO, FiltersListDTO, OptionDTO} from "../../../../../datasources/api-client/data-contracts";
import {ApiRoutes} from "../../../../../datasources/networking/apiRoutes";
import {UpsertFiltersPayload} from "../../../../../datasources/requests/adminFilters";
import {emptyFilterRegistry, FiltersResponse} from "../../../../../datasources/responses/adminUserFilters";
import {useResource} from "../../../../../hooks/useResource";

interface Props {
	id: string;
	onSuccess: () => void;
}

const FiltersComponent: FC<Props> = ({ id, onSuccess }) => {
	const { t } = useTranslation("feature.users");

	const {
		data: filtersData,
		isLoading: filterIsLoading,
		put,
		reload,
	} = useResource<UpsertFiltersPayload, FiltersListDTO>( 
		{ route: ApiRoutes.AdminUserFilters, path: { id } },
		{ },
		{ subjectKey: "filtering" }
	);

	const form = useForm<FiltersResponse>({
		defaultValues: { ...emptyFilterRegistry },
	});

	const handleClickSend = () => {
		const values = form.getValues();
		const payload: UpsertFiltersPayload = {
			filters: values.filters
				.filter((value) => value.value != null && value.value !== "")
				.map((value) => ({
					id: String(value.id),
					values: [String(value.value)],
				})),
		};

		put(payload, { onSuccess }).then(() => reload());
	};

	useEffect(() => {
		form.reset(
			{
				filters:
					filtersData?.items?.map((f: FilterDTO) => ({
						id: f.id,
						name: f.label,
						type: f.dataType,
						options: f.options?.map((o: OptionDTO) => ({ id: o.id, label: o.label })),
						value: f.preselectedValueId,
					})) ?? [],
			},
			{ keepErrors: true }
		);
	}, [filtersData, form]);

	return (
		<>
			<Grid size={12}>
				<FormSectionTitle title={t("filters")} />
			</Grid>

			<Grid container spacing={2}>
				{filterIsLoading && (
					<TableContainer>
						<Table>
							<TableBody>
								<SkeletonTableRow columns={1} rows={3} />
							</TableBody>
						</Table>
					</TableContainer>
				)}

				{!filterIsLoading &&
					filtersData?.items.map((f: FilterDTO, index: number) =>
						f.type === "select" ? (
							<Grid key={f.id} size={6}>
								<FormSelect
									fullWidth
									value={f.preselectedValueId}
									name={`filters.${index}.value`}
									label={f.label}
									options={
										f.options?.map((opt: OptionDTO) => ({
											...opt,
											value: String(opt.id),
										})) || []
									}
									form={form}
									noSelectionOption={t("no option selected", { ns: "common" })}
								/>
							</Grid>
						) : f.type === "text" ? (
							<Grid key={f.id} size={6}>
								<FormTextField
									fullWidth
									value={f.preselectedValue}
									isLoading={filterIsLoading}
									form={form}
									name={`filters.${index}.value`}
									label={f.label}
								/>
							</Grid>
						) : null
					)}

				{!filterIsLoading && (filtersData?.items ?? []).length <= 0 && (
					<Typography variant="body2" color="text.secondary">
						{t("no filters available")}
					</Typography>
				)}

				<Grid>
					<Button
						className="confirm"
						loading={filterIsLoading}
						loadingPosition="start"
						onClick={handleClickSend}
						disabled={!form.formState.isValid}
					>
						{t("apply", { ns: "common" })}
					</Button>
				</Grid>
			</Grid>
		</>
	);
};

export default FiltersComponent;
